import { X, GitBranch, CheckCircle, XCircle, Clock, ExternalLink } from 'lucide-react';
import { GitLabIngressHistory } from '@/types/tenant.types';
import { format } from 'date-fns';

interface IngressHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  history: GitLabIngressHistory[];
  tenantName: string;
}

export default function IngressHistoryModal({
  isOpen,
  onClose,
  history,
  tenantName
}: IngressHistoryModalProps) {
  if (!isOpen) return null;

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-600" />;
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-600" />;
      default:
        return <GitBranch className="w-4 h-4 text-gray-600" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const configs = {
      success: 'bg-green-50 text-green-700 border-green-200',
      failed: 'bg-red-50 text-red-700 border-red-200',
      pending: 'bg-yellow-50 text-yellow-700 border-yellow-200',
    };
    return configs[status as keyof typeof configs] || 'bg-gray-50 text-gray-700 border-gray-200';
  };

  const getActionBadge = (action: string) => {
    const configs = {
      create: { label: 'Create', className: 'bg-blue-50 text-blue-700' },
      update: { label: 'Update', className: 'bg-purple-50 text-purple-700' },
      delete: { label: 'Delete', className: 'bg-red-50 text-red-700' },
      manual_sync: { label: 'Manual Sync', className: 'bg-indigo-50 text-indigo-700' },
    };
    const config = configs[action as keyof typeof configs] || { label: action, className: 'bg-gray-50 text-gray-700' };
    return config;
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className="relative bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <GitBranch className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Ingress Sync History</h2>
                <p className="text-sm text-gray-500">{tenantName}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-auto p-6">
            {history.length === 0 ? (
              <div className="text-center py-12">
                <GitBranch className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No sync history available</p>
              </div>
            ) : (
              <div className="space-y-3">
                {history.slice().reverse().map((entry, index) => (
                  <div
                    key={index}
                    className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      {/* Left side */}
                      <div className="flex items-start gap-3 flex-1">
                        <div className="mt-1">{getStatusIcon(entry.status)}</div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`px-2 py-0.5 text-xs font-medium rounded border ${getStatusBadge(entry.status)}`}>
                              {entry.status.toUpperCase()}
                            </span>
                            <span className={`px-2 py-0.5 text-xs font-medium rounded ${getActionBadge(entry.action).className}`}>
                              {getActionBadge(entry.action).label}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mb-1">
                            {format(new Date(entry.timestamp), 'MMM dd, yyyy HH:mm:ss')}
                          </p>
                          {entry.commit_id && (
                            <div className="flex items-center gap-2">
                              <code className="text-xs bg-white px-2 py-1 rounded border border-gray-200 font-mono">
                                {entry.commit_id.substring(0, 8)}
                              </code>
                              <a
                                href={`https://gitlab.skill-mine.com/skillmine-dev/skillmine-it-infra/gitops/argocd/project_deployment_manifests/-/commit/${entry.commit_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                              >
                                View in GitLab
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            </div>
                          )}
                          {entry.error && (
                            <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                              {entry.error}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50">
            <p className="text-xs text-gray-500">
              Total sync operations: {history.length}
            </p>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition-colors font-medium"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
